/* ==========================================================================
   Tritan — Motion Engine
   GSAP + Lenis initialization, hero mask-reveal, scroll-triggered sections,
   coordinated reveals, parallax, card tilt, counters.
   Executes only after DOMContentLoaded. Progressive enhancement — if JS
   fails, all content is visible via static HTML/CSS.
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {

    /* ------------------------------------------------------------------
       0. PERF GATE — low-end / reduced-motion / save-data devices get native
       scroll and no heavy CSS animations (the .perf-lite class kills them in
       CSS). GSAP reveals + all functional JS still run. Force with ?lite=1.
       (2026-06-10: a slow PC could barely scroll — 200 lqip-shimmer loops +
       Lenis rAF scroll-hijack + backdrop-blur were the cost.)
       ------------------------------------------------------------------ */
    const _params = new URLSearchParams(location.search);
    /* 2026-07-18: pointer:coarse added — deviceMemory is undefined on iOS Safari, so
       every iPhone used to FAIL the low-end check and get the full scroll-hijack stack. */
    const LOW_END = _params.get('lite') === '1'
        || (navigator.deviceMemory && navigator.deviceMemory <= 4)
        || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4)
        || window.matchMedia('(prefers-reduced-motion: reduce)').matches
        || window.matchMedia('(pointer: coarse)').matches
        || (navigator.connection && navigator.connection.saveData);
    if (LOW_END) document.documentElement.classList.add('perf-lite');

    /* ------------------------------------------------------------------
       1. Native scroll everywhere. Lenis removed 2026-07-18 — its rAF
       scroll-hijack loop was the core of the site-wide scroll jank.
       ------------------------------------------------------------------ */

    gsap.registerPlugin(ScrollTrigger);

    /* Scrub/pinned scroll effects run on desktop fine-pointers only. */
    const DESKTOP_MOTION = !LOW_END && window.matchMedia('(pointer: fine)').matches;


    /* ------------------------------------------------------------------
       2. Scroll Progress Bar
       ------------------------------------------------------------------ */
    const progressBar = DESKTOP_MOTION ? document.querySelector('.alpine-scroll-progress') : null;
    if (progressBar) {
        gsap.to(progressBar, {
            scaleX: 1,
            ease: 'none',
            scrollTrigger: {
                trigger: document.documentElement,
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0.3,
            }
        });
    }


    /* ------------------------------------------------------------------
       3. Hero Mask Reveal
       ------------------------------------------------------------------ */
    const heroSection = document.querySelector('.alpine-hero');

    if (heroSection) {
        const maskCircle = heroSection.querySelector('.alpine-hero__mask-circle');
        const headline = heroSection.querySelector('.alpine-hero__headline');
        const subheadline = heroSection.querySelector('.alpine-hero__subheadline');
        const cta = heroSection.querySelector('.alpine-hero__cta');
        const charSpans = heroSection.querySelectorAll('.alpine-hero__char');

        /* Calculate the radius needed to cover the full viewport */
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const maxRadius = Math.sqrt(vw * vw + vh * vh) / 2;

        /* Set circle center to viewport center in pixels (userSpaceOnUse) */
        if (maskCircle) {
            maskCircle.setAttribute('cx', vw / 2);
            maskCircle.setAttribute('cy', vh / 2);
        }

        /* Apply clip-path only when JS runs (progressive enhancement) */
        const heroMedia = heroSection.querySelector('.alpine-hero__media');
        if (heroMedia) {
            heroMedia.style.clipPath = 'url(#alpine-hero-clip)';
            heroMedia.style.webkitClipPath = 'url(#alpine-hero-clip)';
        }

        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        if (maskCircle) {
            tl.fromTo(maskCircle,
                { attr: { r: 0 } },
                { attr: { r: maxRadius }, duration: 0.6 }
            );
        }

        tl.addLabel('textReveal', '+=0.1');

        if (charSpans.length) {
            tl.fromTo(charSpans,
                { y: 40, opacity: 0 },
                { y: 0, opacity: 1, stagger: 0.03, duration: 0.5, ease: 'power2.out' },
                'textReveal'
            );
        }

        if (subheadline) {
            tl.fromTo(subheadline,
                { y: 20, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' },
                '-=0.3'
            );
        }

        if (cta) {
            tl.fromTo(cta,
                { y: 20, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' },
                '-=0.3'
            );
        }
    }


    /* ------------------------------------------------------------------
       4. Scroll-Triggered Feature Sections
       ------------------------------------------------------------------ */
    (DESKTOP_MOTION ? document.querySelectorAll('.alpine-scroll-feature') : []).forEach((section) => {
        const blocks = section.querySelectorAll('.alpine-scroll-feature__block');
        if (!blocks.length) return;

        const blockCount = blocks.length;
        const pinSpacer = blockCount * 100; /* vh per block */

        /* Create the master timeline */
        const scrollTL = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top top',
                end: '+=' + pinSpacer + 'vh',
                pin: true,
                scrub: true,
                onEnter: () => {
                    section.style.willChange = 'transform';
                },
                onLeave: () => {
                    section.style.willChange = 'auto';
                },
                onEnterBack: () => {
                    section.style.willChange = 'transform';
                },
                onLeaveBack: () => {
                    section.style.willChange = 'auto';
                },
            },
        });

        blocks.forEach((block, i) => {
            if (i === 0) {
                /* First block is visible by default — fade out at end of its segment */
                scrollTL.to(block, {
                    opacity: 0,
                    y: -30,
                    duration: 0.4,
                    ease: 'power2.inOut',
                });
            } else if (i === blockCount - 1) {
                /* Last block — fade in and stay */
                scrollTL.fromTo(block,
                    { opacity: 0, y: 40 },
                    { opacity: 1, y: 0, duration: 0.4, ease: 'power2.inOut' }
                );
            } else {
                /* Middle blocks — fade in then out */
                scrollTL
                    .fromTo(block,
                        { opacity: 0, y: 40 },
                        { opacity: 1, y: 0, duration: 0.4, ease: 'power2.inOut' }
                    )
                    .to(block, {
                        opacity: 0,
                        y: -30,
                        duration: 0.4,
                        ease: 'power2.inOut',
                    });
            }
        });
    });


    /* ------------------------------------------------------------------
       5. Coordinated Section Reveals (.alpine-reveal)
       ------------------------------------------------------------------ */
    gsap.utils.toArray('.alpine-reveal').forEach(section => {
        const heading = section.querySelector('.alpine-reveal-heading');
        const text = section.querySelector('.alpine-reveal-text');
        const items = section.querySelectorAll('.alpine-reveal-item');

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top 85%',
                once: true,
            }
        });

        if (heading) tl.from(heading,
            { y: 40, opacity: 0, duration: 0.8, ease: 'power2.out' });
        if (text) tl.from(text,
            { y: 30, opacity: 0, duration: 0.6, ease: 'power2.out' }, '-=0.5');
        if (items.length) tl.from(items,
            {
                y: 20, opacity: 0, duration: 0.5, stagger: 0.06,
                ease: 'power2.out'
            }, '-=0.3');
    });


    /* ------------------------------------------------------------------
       6. Editorial Ghost Text Reveal
       ------------------------------------------------------------------ */
    gsap.utils.toArray('.alpine-editorial').forEach(el => {
        ScrollTrigger.create({
            trigger: el,
            start: 'top 85%',
            once: true,
            onEnter: () => el.classList.add('is-visible'),
        });
    });


    /* ------------------------------------------------------------------
       7. Parallax Image Drift
       ------------------------------------------------------------------ */
    if (DESKTOP_MOTION && document.querySelector('.alpine-parallax')) {
      gsap.utils.toArray('.alpine-parallax').forEach(img => {
          gsap.to(img, {
              yPercent: -15,
              ease: 'none',
              scrollTrigger: {
                  trigger: img.parentElement,
                  start: 'top bottom',
                  end: 'bottom top',
                  scrub: true,
              }
          });
      });
    }


    /* ------------------------------------------------------------------
       8. Product Card Tilt + Shine (Desktop Only)
       ------------------------------------------------------------------ */
    if (window.matchMedia('(min-width: 768px)').matches) {
        document.querySelectorAll('.alpine-card-tilt').forEach(card => {
            const shine = card.querySelector('.alpine-card-shine');
            card.addEventListener('mousemove', (e) => {
                const rect = card.getBoundingClientRect();
                const x = (e.clientX - rect.left) / rect.width - 0.5;
                const y = (e.clientY - rect.top) / rect.height - 0.5;
                card.style.transform =
                    `perspective(600px) rotateY(${x * 6}deg) rotateX(${y * -6}deg)`;
                if (shine) {
                    shine.style.background =
                        `radial-gradient(circle at ${(x + 0.5) * 100}% ${(y + 0.5) * 100}%,
                        rgba(197,160,89,0.08) 0%, transparent 60%)`;
                }
            });
            card.addEventListener('mouseleave', () => {
                card.style.transform = '';
                if (shine) shine.style.background = '';
            });
        });
    }


    /* ------------------------------------------------------------------
       9. Scroll-Triggered Number Counters
       ------------------------------------------------------------------ */
    gsap.utils.toArray('.alpine-counter').forEach(el => {
        const target = parseInt(el.dataset.target, 10);
        if (isNaN(target)) return;
        gsap.from(el, {
            textContent: 0,
            duration: 2,
            ease: 'power1.out',
            snap: { textContent: 1 },
            scrollTrigger: {
                trigger: el,
                start: 'top 85%',
                once: true,
            }
        });
    });


    /* ------------------------------------------------------------------
       10. ATC Morph Feedback
       ------------------------------------------------------------------ */
    document.addEventListener('alpine:cart-updated', () => {
        /* Cart icon bump */
        const cartIcon = document.querySelector('.alpine-cart-icon');
        if (cartIcon) {
            cartIcon.classList.add('is-bumped');
            cartIcon.addEventListener('animationend', () => {
                cartIcon.classList.remove('is-bumped');
            }, { once: true });
        }
    });


    /* ------------------------------------------------------------------
       11. Generic Reveal-on-Scroll (legacy utility)
       ------------------------------------------------------------------ */
    document.querySelectorAll('[data-reveal]').forEach((el) => {
        gsap.fromTo(el,
            { y: 30, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 0.6,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: el,
                    start: 'top 85%',
                    once: true,
                },
            }
        );
    });


    /* ------------------------------------------------------------------
       12. Advisory Board — Stagger Reveal
       ------------------------------------------------------------------ */
    const advisoryCards = document.querySelectorAll('[data-advisory-card]');
    if (advisoryCards.length) {
        gsap.fromTo(advisoryCards,
            { y: 30, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 0.6,
                stagger: 0.1,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: advisoryCards[0].closest('.alpine-advisory'),
                    start: 'top 80%',
                    once: true,
                },
            }
        );
    }

});

// TRITAN STANDARD — Single global observer registry
// Added before C5 to prevent IntersectionObserver stacking
window.TritanObservers = window.TritanObservers || {
  _registry: new Map(),
  register: function(key, element, callback, options) {
    options = options || {};
    if (this._registry.has(key)) return; // prevent duplicate
    var obs = new IntersectionObserver(callback, options);
    obs.observe(element);
    this._registry.set(key, obs);
  }
};

// C3 — Judge.me Star Animation — v3.0 Revamp
(function() {
  if (window.__tritanStarInit) return;
  window.__tritanStarInit = true;

  function wireStars(container) {
    const filled = container.querySelectorAll('.jdgm-star.jdgm--on');
    if (!filled.length) return;

    // Add Tritan animation classes — do not touch Judge.me's own classes
    filled.forEach((star, i) => {
      star.classList.add('tritan-star--filled');
      star.style.transitionDelay = `${i * 60}ms`;
    });

    window.TritanObservers.register(
      'stars-' + Date.now(),
      container,
      (entries) => {
        entries.forEach(e => {
          if (!e.isIntersecting) return;
          container.classList.add('tritan-stars--animate');
        });
      },
      { threshold: 0.5 }
    );
  }

  // MutationObserver — catches async Judge.me injection
  const domWatcher = new MutationObserver((mutations) => {
    mutations.forEach(m => {
      m.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (
          node.classList?.contains('jdgm-prev-badge') ||
          node.classList?.contains('jdgm-rev-widg') ||
          node.querySelector?.('.jdgm-star')
        ) {
          wireStars(node);
          domWatcher.disconnect();
        }
      });
    });
  });

  const pdp = document.querySelector('.product__info-wrapper, .pdp-main, main');
  if (pdp) domWatcher.observe(pdp, { childList: true, subtree: true });

  // Synchronous fallback — if Judge.me already rendered
  ['.jdgm-prev-badge', '.jdgm-rev-widg'].forEach(sel => {
    const el = document.querySelector(sel);
    if (el) { wireStars(el); domWatcher.disconnect(); }
  });
})();

// B4 — Sold Count JS Formatting (Option A Client-Side Fix)
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.pdp-sold-count').forEach(el => {
    [...el.childNodes].filter(n => n.nodeType === 3).forEach(node => {
      node.textContent = node.textContent.replace(/\b(\d{4,})\b/g,
        n => parseInt(n).toLocaleString()
      );
    });
  });
});

// C2 — Ingredient Drawer Modal Logic
document.addEventListener('DOMContentLoaded', () => {
  const triggers = document.querySelectorAll('.pdp-ingredient-trigger');
  const modal = document.getElementById('ingredient-modal');
  
  if (!modal || triggers.length === 0 || !window.TritanIngredients) return;

  const titleEl = document.getElementById('modal-name');
  const funcEl = document.getElementById('modal-function');
  const doseEl = document.getElementById('modal-dose');
  const originEl = document.getElementById('modal-origin');
  const originContainer = document.getElementById('modal-origin-container');
  const testedContainer = document.getElementById('modal-tested-container');
  const researchEl = document.getElementById('modal-research');
  const researchContainer = document.getElementById('modal-research-container');
  const closeBtns = modal.querySelectorAll('.ingredient-drawer__back, .ingredient-drawer__close');

  triggers.forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-ingredient-id');
      const data = window.TritanIngredients[id];
      if (!data) return;

      // Populate Modal UI
      titleEl.textContent = data.name || '';
      funcEl.textContent = data.function || '';
      doseEl.textContent = data.dose || '—';
      
      if (data.origin) {
        originContainer.style.display = 'block';
        originEl.textContent = data.origin;
      } else {
        originContainer.style.display = 'none';
      }

      testedContainer.style.display = data.tested ? 'block' : 'none';

      if (data.research && data.research.length > 0) {
        researchContainer.style.display = 'block';
        const refs = Array.isArray(data.research) ? data.research : [data.research];
        researchEl.innerHTML = refs.map(url => {
          let cleanUrl = url.trim();
          if(!cleanUrl) return '';
          return cleanUrl.startsWith('http') 
            ? `<a href="${cleanUrl}" target="_blank" rel="noopener" style="display:block; color:var(--eyebrow-ink, #1A233A) ; font-size:13px; text-decoration:none; margin-bottom:4px;">View study →</a>`
            : `<span style="display:block; font-size:13px; color:rgba(26,35,58,0.65); margin-bottom:4px;">${cleanUrl}</span>`;
        }).join('');
      } else {
        researchContainer.style.display = 'none';
      }

      modal.showModal();
    });
  });

  closeBtns.forEach(btn => btn.addEventListener('click', () => modal.close()));
  
  // Close on backdrop click (click outside)
  modal.addEventListener('click', (e) => {
    const dialogBounds = modal.getBoundingClientRect();
    if (
      e.clientX < dialogBounds.left ||
      e.clientX > dialogBounds.right ||
      e.clientY < dialogBounds.top ||
      e.clientY > dialogBounds.bottom
    ) {
      modal.close();
    }
  });
});

/* C5 exit-intent block REMOVED 2026-07-18 — it was a dead duplicate of the snippet's
   own exit-intent (its `is-active` class has no CSS rule, so it could never display)
   that still cost a scroll listener, a mouseleave listener, and a 20s timer. The
   single live implementation is in snippets/alpine-exit-intent.liquid. */

console.log('[Tritan] Observer registry loaded:', !!window.TritanObservers);

